import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Link,
  Select,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  VStack,
  useBreakpointValue,
  useToast,
} from '@chakra-ui/react';
import { RegisterData, useUserContext } from '@providers/useUserContext';
import { useClientRefereralTypes } from 'hooks/useApiQueries';
import { t } from 'i18next';
import { useMemo } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import PhoneInput from 'react-phone-number-input';
import { useLocation, useNavigate } from 'react-router';
import { ApiClientExceptionErrorCode } from 'types/api';
import { ApiClientException } from 'utils/apiClientException';
import { HTTP } from 'utils/http';
import FloatingLabelInput from '../../components/FloatingLabelInput';
import('react-phone-number-input/style.css');

interface LoginFormValues {
  email: string;
  password: string;
}

const AuthTabs = () => {
  const tabVariant = useBreakpointValue({ base: 'soft-rounded', md: 'enclosed' });
  const ReferralClientId = useGetReferalId();

  return (
    <Tabs isFitted={tabVariant === 'soft-rounded'} align="center" defaultIndex={ReferralClientId ? 1 : 0}>
      <TabList mb="1em">
        <Tab>Se connecter</Tab>
        <Tab>S'inscrire</Tab>
      </TabList>
      <TabPanels>
        <TabPanel>
          <LoginForm />
        </TabPanel>
        <TabPanel>
          <RegisterForm />
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};

const Auth = () => {
  const { isLoggedIn, logout } = useUserContext();

  return (
    <Box w="full" p="0" className={isLoggedIn ? 'accountAuthLoggedIn' : 'accountAuthNotLoggedIn'}>
      {isLoggedIn ? <Button onClick={logout}>Logout</Button> : <AuthTabs />}
    </Box>
  );
};

export function LoginForm() {
  const { state: locationState } = useLocation();
  const navigate = useNavigate();
  const toast = useToast({ id: 'migration' });
  const methods = useForm<LoginFormValues>({});
  const {
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = methods;
  const { login } = useUserContext();

  async function onSubmit({ email, password }: LoginFormValues) {
    try {
      await login(email, password);
      if (locationState && locationState.onLoginRedirect) {
        navigate(locationState.onLoginRedirect, { state: null });
      }
    } catch (error) {
      if (error instanceof ApiClientException) {
        const message = error.getMessage();
        if (error.ErrorCode === ApiClientExceptionErrorCode.ClientMissingFromSite) {
          try {
            toast({ title: t("Votre compte client n'existe pas encore sur cette marque, nous allons le migrer") });
            await HTTP.post('/auth/migrate-user', { email }).send();
            toast({ title: t('Migration terminée'), status: 'success' });
            await login(email, password);
            return;
          } catch (error) {
            if (error instanceof ApiClientException) {
              const message = error.getMessage();
              setError('root', { message });
            }

            return setError('root', { message: t('La migration a échouée') });
          }
        }

        if (error.ErrorCode === ApiClientExceptionErrorCode.InvalidPassword) {
          setError('password', { message });
        } else {
          setError('root', { message });
        }
      }
    }
  }

  return (
    <FormProvider {...methods}>
      <FormControl isInvalid={!!errors.root}>
        <VStack className="loginForm" align="stretch" as="form" onSubmit={handleSubmit(onSubmit)}>
          <FormControl isInvalid={!!errors.email}>
            <FloatingLabelInput
              name="email"
              label="E-mail"
              type="email"
              registerOptions={{ required: 'Veuillez entrer un email valide' }}
              errorMessage="Veuillez entrer un email valide"
            />
          </FormControl>

          <FormControl isInvalid={!!errors.password}>
            <FloatingLabelInput
              name="password"
              label="Mot de passe"
              type="password"
              registerOptions={{ required: 'Veuillez entrer un mot de passe valide' }}
              errorMessage="Veuillez entrer un mot de passe valide"
            />
            <FormErrorMessage alignSelf="center" justifyContent={'center'}>
              {errors.root?.message}
            </FormErrorMessage>
          </FormControl>
          <FormErrorMessage alignSelf="center" justifyContent={'center'}>
            {errors.root?.message}
          </FormErrorMessage>
          <VStack padding="2em 1em">
            <Button
              type="submit"
              textAlign="center"
              alignContent={'center'}
              margin="auto"
              isLoading={isSubmitting}
              variant="contained"
            >
              Me connecter
            </Button>
            <Link color="blue.500" onClick={() => navigate('/account/reset-password')} style={{ marginTop: '1em' }}>
              Mot de passe oublié?
            </Link>
          </VStack>
        </VStack>
      </FormControl>
    </FormProvider>
  );
}

function useGetReferalId() {
  const { search } = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const referralQueryParam = params.get('referral') ?? '';
  try {
    const referral = JSON.parse(atob(referralQueryParam));
    return referral.Id;
  } catch (error) {
    return null;
  }
}
export function RegisterForm() {
  const { state: locationState } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const ReferralClientId = useGetReferalId();

  const methods = useForm<RegisterData>({
    defaultValues: {
      Newsletter: true,
      ReferredBy: ReferralClientId ? 'Another Client' : undefined,
    },
  });

  const {
    register,
    handleSubmit,
    setError,
    getValues,
    setValue,
    formState: { errors, isSubmitting },
  } = methods;

  const { signUp } = useUserContext();

  const { data: referalTypes } = useClientRefereralTypes();

  async function onSubmit(data: RegisterData) {
    try {
      if (ReferralClientId) data.ReferralClientId = ReferralClientId;
      await signUp(data);
      if (locationState && locationState.onLoginRedirect) {
        navigate(locationState.onLoginRedirect, { state: null });
      } else {
        navigate('.');
      }
    } catch (error) {
      if (error instanceof ApiClientException) {
        const message = error.getMessage();

        if (error.ErrorCode === ApiClientExceptionErrorCode.EmailUsed) {
          setError('Email', { message });
          setError('root', { message: t('Veuillez vérifier vos informations') });
        } else {
          setError('root', { message });
        }
      }
    }
  }
  return (
    <FormProvider {...methods}>
      <VStack className="signUpForm" align="stretch" as="form" onSubmit={handleSubmit(onSubmit)}>
        <FormControl isInvalid={!!errors.Email}>
          <FloatingLabelInput
            name="Email"
            label="E-mail*"
            type="email"
            registerOptions={{ required: t('Veuillez entrer un email valide') }}
            errorMessage={errors.Email?.message}
          />
        </FormControl>
        <FormControl>
          <FloatingLabelInput
            name="Password"
            label={t('Mot de passe') + '*'}
            type="password"
            registerOptions={{ required: 'Veuillez entrer un mot de passe valide' }}
            errorMessage={t('Veuillez entrer un mot de passe valide')}
          />
        </FormControl>

        <FormControl>
          <FloatingLabelInput name="FirstName" label="Prénom*" type="text" registerOptions={{ required: true }} />
        </FormControl>

        <FormControl>
          <FloatingLabelInput name="LastName" label="Nom*" type="text" registerOptions={{ required: true }} />
        </FormControl>

        <FormControl>
          <FloatingLabelInput
            name="BirthDate"
            label="Date de naissance*"
            type="date"
            registerOptions={{ required: true }}
          />
        </FormControl>

        <FormControl>
          <Box maxWidth={'400px'}>
            <FormLabel textTransform={'uppercase'}>Téléphone*</FormLabel>
            <PhoneInput
              international
              required
              inputComponent={Input}
              defaultCountry="FR"
              numberInputProps={register('MobilePhone')}
              onChange={(value) => {
                if (value) setValue('MobilePhone', value);
              }}
            />
          </Box>
        </FormControl>

        <FormControl isInvalid={!!errors.ReferredBy}>
          <Box maxWidth={'400px'}>
            <FormLabel textTransform={'uppercase'}>Comment avez-vous entendu parler de nous ?</FormLabel>
            {referalTypes && (
              <Select
                placeholder="Sélectionner une option"
                {...register('ReferredBy', {})}
                isDisabled={!!ReferralClientId && !!getValues('ReferredBy')}
              >
                {referalTypes.map((referalType, index) => (
                  <option key={index} value={referalType}>
                    {referalType}
                  </option>
                ))}
              </Select>
            )}
          </Box>
        </FormControl>

        <VStack>
          <Box maxWidth="400px" width="100%">
            <FormControl textAlign={'left'}>
              <Checkbox checked={true} borderColor="black" {...register('Newsletter')} textAlign={'left'}>
                Rester informé du planning, des événements et des offres
              </Checkbox>
            </FormControl>
          </Box>

          <Box maxWidth="400px" width="100%">
            <FormControl isInvalid={!!errors.TermsAndConditions} textAlign={'left'}>
              <Checkbox borderColor="black" {...register('TermsAndConditions', { required: true })} textAlign={'left'}>
                Je comprends et j’accepte les{' '}
                <Link textDecoration="underline" href="#">
                  conditions générales de vente*
                </Link>
              </Checkbox>
            </FormControl>
          </Box>
        </VStack>

        <FormControl isInvalid={!!errors.root} alignSelf="center" justifyContent={'center'} mt={1}>
          <FormErrorMessage textAlign="center" justifyContent={'center'}>
            {errors.root?.message}
          </FormErrorMessage>
        </FormControl>
        <VStack padding="2em 1em">
          <Button textAlign="center" margin="auto" type="submit" variant="contained" isLoading={isSubmitting}>
            Créer un compte
          </Button>
        </VStack>
      </VStack>
    </FormProvider>
  );
}

export function ShoeSizeSelect() {
  const { register, watch } = useFormContext<RegisterData>();
  const shoeSize = watch('CustomFields.ShoeSize');
  const { t } = useTranslation();
  const min = 25;
  const max = 50;
  const options = Array.from({ length: max - min }, (_, i) => i + min);

  return (
    <FormControl>
      <Box maxWidth={'400px'}>
        <FormLabel>{t('Pointure de chaussure*')}</FormLabel>
        <Select required {...register('CustomFields.ShoeSize', { valueAsNumber: true })} value={shoeSize || ''}>
          <option disabled value="">
            {t('Sélectionner une pointure')}
          </option>
          {options.map((option, index) => (
            <option key={index} value={option}>
              {option}
            </option>
          ))}
        </Select>
      </Box>
    </FormControl>
  );
}

export default Auth;
